import { useWorkspaceText } from '../interface-copy';
import { useEffect, useState, type ReactNode } from 'react';
import { WorkspaceIcon } from '../icons';
import type { PageSuggestion } from './types';

/**
 * On a phone the suggestions sit in a bottom sheet, one at a time, above the
 * page text. The grabber folds the sheet down to one line so the page can be
 * read; the pager steps through the suggestions and follows a marker the
 * person taps in the text.
 */
export function SuggestionSheet({
  suggestions,
  focused,
  onFocus,
  renderCard,
}: {
  suggestions: PageSuggestion[];
  focused: string | null;
  onFocus: (id: string) => void;
  renderCard: (suggestion: PageSuggestion, pager: ReactNode) => ReactNode;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const [open, setOpen] = useState(true);
  const position = Math.max(
    0,
    suggestions.findIndex((suggestion) => suggestion.id === focused),
  );
  const current = suggestions[Math.min(position, suggestions.length - 1)];
  // A marker tapped in the text opens the sheet on its suggestion.
  useEffect(() => {
    if (focused) setOpen(true);
  }, [focused]);
  if (!current) return null;
  const step = (delta: number) => {
    const next = suggestions[(position + delta + suggestions.length) % suggestions.length];
    if (next) onFocus(next.id);
  };
  const pager = (
    <div className="ws-suggestion-pager">
      <button
        type="button"
        className="ws-icon-button"
        aria-label={uiText('Previous suggestion')}
        disabled={suggestions.length < 2}
        onClick={() => step(-1)}
      >
        <WorkspaceIcon name="chevron" size={16} className="ws-back-arrow" />
      </button>
      <span aria-live="polite">
        {uiRich('Suggestion {value1} of {value2}', {
          value1: position + 1,
          value2: suggestions.length,
        })}
      </span>
      <button
        type="button"
        className="ws-icon-button"
        aria-label={uiText('Next suggestion')}
        disabled={suggestions.length < 2}
        onClick={() => step(1)}
      >
        <WorkspaceIcon name="chevron" size={16} />
      </button>
    </div>
  );
  return (
    <section
      className="ws-suggestion-sheet"
      data-open={open ? 'true' : 'false'}
      aria-label={uiText('Suggestions')}
    >
      <button
        type="button"
        className="ws-suggestion-sheet-handle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true" />
        <span className="ws-sr-only">
          {open ? uiText('Fold the suggestions away') : uiText('Show the suggestions')}
        </span>
        {!open && (
          <span className="ws-suggestion-sheet-summary">
            {suggestions.length === 1
              ? uiText('1 suggestion')
              : uiText('{value1} suggestions', { value1: suggestions.length })}
          </span>
        )}
      </button>
      {open && renderCard(current, pager)}
    </section>
  );
}
